import React, { useState, useEffect, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import { getTeamLogoUrl as getTeamLogoUrlDirect } from '../utils/formatters';
import { BOOKMAKER_ALIASES } from '../utils/constants';
import { useBooks } from '../contexts/BooksContext';
import GameHeader from './GameHeader';
import H2hMiniTable from './H2hMiniTable';
import H2hAllTable from './H2hAllTable';
import TotalsMiniTable from './TotalsMiniTable';
import SpreadsMiniTable from './SpreadsMiniTable';
import TotalsTable from './TotalsTable';
import SpreadsTable from './SpreadsTable';
import NFLPlayerModal from './NFLPlayerModal';
import UpgradeButton from './UpgradeButton';

const MobileNFLDataTable = ({
  data,
  gameTime,
  gameStatus,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  selectedMarket,
  bookmakers,
  isLoading,
  showTiers,
  onTierToggle,
  masterTierFilter,
  onOddsClick,
  clickedCellId,
  isCollapsed = false,
  onCollapseToggle,
  onSettingsClick,
  nflViewMode = 'ou' // 'ou' for O/U view, 'bestline' for Edge view
}) => {

  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', sport: 'nfl', ...game };

  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Track rendering state - prevent heavy processing until spinner paints
  const [isRendering, setIsRendering] = useState(false);
  const prevMarketRef = useRef(selectedMarket);

  // Detect market change DURING render (not in useEffect)
  if (prevMarketRef.current !== selectedMarket) {
    if (!isRendering) {
      // Market just changed, set rendering state synchronously
      setIsRendering(true);
    }
    prevMarketRef.current = selectedMarket;
  }

  // Clear rendering state after spinner has painted
  useEffect(() => {
    if (isRendering) {
      const timeoutId = setTimeout(() => {
        setIsRendering(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isRendering]);

  // Combined loading state
  const showLoading = isLoading || isRendering;

  // Block rendering heavy data while loading
  const displayData = showLoading ? [] : data;

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = React.useState(0);
  const booksPerPage = 5;

  // Player modal state
  const [selectedPlayer, setSelectedPlayer] = React.useState(null);
  const [showPlayerModal, setShowPlayerModal] = React.useState(false);

  // View mode toggle state (mini vs detail)
  const [viewMode, setViewMode] = React.useState('mini'); // 'mini' or 'detail'
  // Best Bets book filter — gates which books participate in the Middles
  // calculation (matches the setting on the Settings screen).
  const { isBookEnabledForBestBets } = useBooks();

  const handlePlayerClick = (playerName, playerTeam, playerPosition, playerId) => {
    setSelectedPlayer({ playerName, playerTeam, playerPosition, playerId });
    setShowPlayerModal(true);
  };

  const handleClosePlayerModal = () => {
    setShowPlayerModal(false);
    setSelectedPlayer(null);
  };

  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);

  // Get visible books for mobile
  const visibleBookmakers = allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage);

  // Format the live status
  const formatNFLLiveStatus = (status) => {
    if (!status || status === 'Scheduled') return '';

    // Check for end game states
    if (status === 'InProgress') return 'Live';
    if (status === 'Final' || status === 'Completed') return 'Final';
    if (status === 'Final/OT') return 'Final/OT';
    if (status === 'Postponed') return 'Postponed';

    // Handle quarter/half format
    if (status.includes('Q') || status.includes('Half')) {
      return status;
    }

    // Return as-is for any other status
    return status;
  };

  // Tooltip text function (for tier colors) - moved here to be available for H2H
  const getTooltipText = (price, bestBookPrice, bestBookDiff, count) => {
    if (price === bestBookPrice && count > 1) {
      const diffValue = Math.abs(bestBookDiff);
      if (diffValue >= 0.25) {
        return 'Best odds - Tier 1 edge';
      } else if (diffValue >= 0.15) {
        return 'Best odds - Tier 2 edge';
      } else if (diffValue >= 0.05) {
        return 'Best odds - Tier 3 edge';
      }
      return 'Best odds';
    }
    return '';
  };

  // Tier color styling function - moved here to be available for H2H
  const getCellStyle = (price, bestBookPrice, bestBookDiff, count, allPlayerLines = null, currentBook = null, market = null) => {
    const style = {
      fontWeight: 'bold',
      fontSize: '0.8rem'
    };

    // For Edge View, count how many books have this exact odds across all lines
    let occurrences = 1;
    if (allPlayerLines && currentBook && market) {
      occurrences = 0;
      const bookAlias = BOOKMAKER_ALIASES[currentBook] || currentBook;

      // Check all visible books to see if they also have this odds value
      visibleBookmakers.forEach(book => {
        const checkAlias = BOOKMAKER_ALIASES[book] || book;
        allPlayerLines.forEach(line => {
          const bookOdds = line[`${market}_${checkAlias}_over`] || line[`${market}_${checkAlias}`] ||
                          line[`${market}_${book}_over`] || line[`${market}_${book}`];
          if (bookOdds === price) {
            occurrences++;
          }
        });
      });
    }

    // Always bold best odds when there are multiple books and it's the unique best price
    if (price === bestBookPrice && count > 1 && occurrences === 1) {

      // Apply edge tier colors if they meet the criteria
      if (bestBookDiff !== 1) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          // Tier 1: Green with yellow text (original design)
          Object.assign(style, {
            backgroundColor: 'green',
            color: 'yellow',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        } else if (diffValue >= 0.15) {
          // Tier 2: Amber with navy blue text for better contrast
          Object.assign(style, {
            backgroundColor: '#FFC107',
            color: '#212529',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        } else if (diffValue >= 0.05) {
          // Tier 3: Blue - subtle, draws less attention than tier 1 & 2
          Object.assign(style, {
            backgroundColor: '#3B82F6',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        }
      }
    }

    return style;
  };

  // Calculate best line for a player/book combo based on edge (for Edge View)
  // Takes all lines for a player and finds the OVER with best edge for the specified book
  const getBestLineByEdge = (allPlayerLines, book, market) => {
    let bestEdgeOverLine = null;
    let bestEdgeDiff = -Infinity;
    let highestPointMainLine = null;
    let highestPoint = -Infinity;

    // Handle bookmaker aliases (e.g., williamhill_us -> caesars)
    const bookAlias = BOOKMAKER_ALIASES[book] || book;

    // Iterate through all lines (different point values) for this player
    allPlayerLines.forEach(line => {
      // Try both the original book name and its alias
      const overOdds = line[`${market}_${bookAlias}_over`] || line[`${market}_${bookAlias}`] ||
                       line[`${market}_${book}_over`] || line[`${market}_${book}`];
      const underOdds = line[`${market}_${bookAlias}_under`] || line[`${market}_${book}_under`];
      const bookPoint = line.point;
      const bestBookPrice = line[`${market}_bestBookPrice`];
      const bestBookDiff = line[`${market}_bestBookDiff`];
      const isAlternate = line.isAlternate;

      // Skip if this book has no over odds for this line
      if (overOdds === null || overOdds === undefined) {
        return;
      }

      // Skip if odds are -200 or worse (only consider odds > -200)
      if (overOdds <= -200) {
        return;
      }

      // Track highest point MAINLINE as fallback when no edges exist (only if odds > -200)
      // Don't use alternates as fallback - only show alternates if they have real edges
      if (!isAlternate && bookPoint > highestPoint) {
        highestPoint = bookPoint;
        highestPointMainLine = {
          overOdds,
          bookPoint,
          bestBookPrice,
          bestBookDiff
        };
      }

      // Check if this OVER has an edge
      // Edge means this book's over equals the best price AND has a meaningful diff
      if (overOdds === bestBookPrice && bestBookDiff !== null && bestBookDiff !== undefined && bestBookDiff > bestEdgeDiff) {
        bestEdgeDiff = bestBookDiff;
        bestEdgeOverLine = {
          odds: overOdds,
          point: bookPoint,
          side: 'over',
          bestBookPrice,
          bestBookDiff
        };
      }
    });

    // If we found an over with edge, return it
    if (bestEdgeOverLine) {
      return bestEdgeOverLine;
    }

    // Otherwise, return the highest point mainline over for this book
    if (highestPointMainLine) {
      const { overOdds, bookPoint, bestBookPrice, bestBookDiff } = highestPointMainLine;
      return {
        odds: overOdds,
        point: bookPoint,
        side: 'over',
        bestBookPrice,
        bestBookDiff
      };
    }

    return null;
  };

  // Handle H2H, Totals, and Spreads markets
  if (selectedMarket === 'h2h') {
    // Check if data exists
    if (!data || data.length === 0) {
      return (
        <div className="table-group">
          <GameHeader
            gameTime={gameTime}
            gameStatus={gameStatus}
            game={game}
            getTeamLogoUrl={getTeamLogoUrl}
            isMobile={true}
            isCollapsed={isCollapsed}
            onCollapseToggle={onCollapseToggle}
            formatLiveStatus={formatNFLLiveStatus}
            isLoading={showLoading}
            showScores={true}
            isLoading={showLoading}
          />
          {!isCollapsed && (
            <div className="table-container">
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td colSpan={2} className="text-center" style={{ padding: '20px', color: '#999' }}>
                      No moneyline odds available
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </div>
      );
    }

    // Separate data by market type (like production does)
    let h2hData = [];
    let totalsData = [];
    let spreadsData = [];

    data.forEach(player => {
      if (player.playerId?.startsWith('totals_')) {
        totalsData.push(player);
      } else if (player.playerId?.startsWith('spreads_')) {
        spreadsData.push(player);
      } else if (player.teamId === 'away' || player.teamId === 'home') {
        h2hData.push(player);
      }
    });

    // Sort h2h data (away first, then home)
    h2hData.sort((a, b) => {
      if (a.team === 'away' && b.team === 'home') return -1;
      if (a.team === 'home' && b.team === 'away') return 1;
      return 0;
    });

    return (
      <div className="table-group">
        <GameHeader
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={true}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatNFLLiveStatus}
            isLoading={showLoading}
          showScores={true}
            isLoading={showLoading}
        />

        {/* View Mode Toggle */}
        {!isCollapsed && selectedMarket === 'h2h' && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '8px 0' }}>
            <button
              className={`btn btn-sm ${viewMode === 'mini' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('mini')}
              style={{ fontSize: '12px', padding: '4px 12px' }}
            >
              Best Odds
            </button>
            <button
              className={`btn btn-sm ${viewMode === 'detail' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('detail')}
              style={{ fontSize: '12px', padding: '4px 12px' }}
            >
              All Books
            </button>
          </div>
        )}

        {!isCollapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {viewMode === 'mini' ? (
              <>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <H2hMiniTable
                    h2hData={h2hData}
                    getBookLogoUrl={getBookLogoUrl}
                    getTeamLogoUrl={getTeamLogoUrlDirect}
                    getCellStyle={getCellStyle}
                    getTooltipText={getTooltipText}
                    onOddsClick={onOddsClick}
                    game={safeGame}
                    periodsToShow={['Game']}
                  />
                  <TotalsMiniTable
                    totalsData={totalsData}
                    getBookLogoUrl={getBookLogoUrl}
                    getCellStyle={getCellStyle}
                    getTooltipText={getTooltipText}
                    onOddsClick={onOddsClick}
                    game={safeGame}
                    periodsToShow={['Game']}
                  />
                </div>
                <SpreadsMiniTable
                  spreadsData={spreadsData}
                  getBookLogoUrl={getBookLogoUrl}
                  getTeamLogoUrl={getTeamLogoUrlDirect}
                  getCellStyle={getCellStyle}
                  getTooltipText={getTooltipText}
                  onOddsClick={onOddsClick}
                  game={safeGame}
                  periodsToShow={['Game']}
                />
              </>
            ) : (
              <>
                {h2hData.length > 0 && (
                  <H2hAllTable
                    h2hData={h2hData}
                    bookLogos={visibleBookmakers}
                    getBookLogoUrl={getBookLogoUrl}
                    getCellStyle={getCellStyle}
                    getTooltipText={getTooltipText}
                    onOddsClick={onOddsClick}
                    game={safeGame}
                    oddsCellStyle={{ minWidth: '60px' }}
                    periodsToShow={['Game']}
                  />
                )}
                {totalsData.length > 0 && (
                  <TotalsTable
                    totalsData={totalsData}
                    bookLogos={visibleBookmakers}
                    getBookLogoUrl={getBookLogoUrl}
                    getCellStyle={getCellStyle}
                    getTooltipText={getTooltipText}
                    onOddsClick={onOddsClick}
                    game={safeGame}
                    oddsCellStyle={{ minWidth: '60px' }}
                  />
                )}
                {spreadsData.length > 0 && (
                  <SpreadsTable
                    spreadsData={spreadsData}
                    bookLogos={visibleBookmakers}
                    getBookLogoUrl={getBookLogoUrl}
                    getCellStyle={getCellStyle}
                    getTooltipText={getTooltipText}
                    onOddsClick={onOddsClick}
                    game={safeGame}
                    oddsCellStyle={{ minWidth: '60px' }}
                  />
                )}
              </>
            )}

            {/* Mobile Book Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '15px',
                marginTop: '10px'
              }}>
                <button
                  onClick={() => setMobileBookIndex(Math.max(0, mobileBookIndex - 1))}
                  disabled={mobileBookIndex === 0}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: mobileBookIndex === 0 ? 'default' : 'pointer',
                    opacity: mobileBookIndex === 0 ? 0.3 : 1,
                    padding: '5px'
                  }}
                >
                  ‹
                </button>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {[...Array(totalPages)].map((_, index) => (
                    <div
                      key={index}
                      onClick={() => setMobileBookIndex(index)}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: index === mobileBookIndex ? '#007bff' : '#dee2e6',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                      }}
                    />
                  ))}
                </div>

                <button
                  onClick={() => setMobileBookIndex(Math.min(totalPages - 1, mobileBookIndex + 1))}
                  disabled={mobileBookIndex === totalPages - 1}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: mobileBookIndex === totalPages - 1 ? 'default' : 'pointer',
                    opacity: mobileBookIndex === totalPages - 1 ? 0.3 : 1,
                    padding: '5px'
                  }}
                >
                  ›
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (selectedMarket === 'totals') {
    return <TotalsTable
      data={data}
      game={safeGame}
      bookmakers={visibleBookmakers}
      getBookLogoUrl={getBookLogoUrl}
      getTeamLogoUrl={getTeamLogoUrl}
      isMobile={true}
      onOddsClick={onOddsClick}
      showTiers={showTiers}
      masterTierFilter={masterTierFilter}
      gameTime={gameTime}
      gameStatus={gameStatus}
      isCollapsed={isCollapsed}
      onCollapseToggle={onCollapseToggle}
      formatLiveStatus={formatNFLLiveStatus}
            isLoading={showLoading}
    />;
  }

  if (selectedMarket === 'spreads') {
    return <SpreadsTable
      data={data}
      game={safeGame}
      bookmakers={visibleBookmakers}
      getBookLogoUrl={getBookLogoUrl}
      getTeamLogoUrl={getTeamLogoUrl}
      isMobile={true}
      onOddsClick={onOddsClick}
      showTiers={showTiers}
      masterTierFilter={masterTierFilter}
      gameTime={gameTime}
      gameStatus={gameStatus}
      isCollapsed={isCollapsed}
      onCollapseToggle={onCollapseToggle}
      formatLiveStatus={formatNFLLiveStatus}
            isLoading={showLoading}
    />;
  }

  // Handle empty or loading data
  if (!displayData || displayData.length === 0 || showLoading) {
    return (
      <div className="table-group">
        <GameHeader
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={true}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatNFLLiveStatus}
            isLoading={showLoading}
          showScores={true}
            isLoading={showLoading}
        />

        {!isCollapsed && (
          <div className="table-container">
            <Table bordered size="sm">
              <tbody>
                <tr>
                  <td colSpan={2} className="text-center" style={{ padding: '20px', color: '#999' }}>
                    {isLoading ? 'Loading odds...' : (
                      (game?.gameIDmlb?.startsWith('locked_') || game?.gameId?.startsWith('locked_')) ? (
                        <UpgradeButton onClick={onSettingsClick} />
                      ) : (
                        'No odds available'
                      )
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}
      </div>
    );
  }

  // Process player props data
  let filteredData = displayData;

  // Check if this is a yards market (tier filtering should not apply)
  const isYardsMarket = ['player_pass_yds', 'player_rush_yds', 'player_rush_attempts', 'player_reception_yds', 'player_receptions', 'player_rush_reception_yds', 'player_field_goals'].includes(selectedMarket);

  // Check if this is a binary market (First TD, Last TD, etc.) - these have 'yes' as point value
  const isBinaryMarket = selectedMarket === 'player_1st_td' ||
                         selectedMarket === 'player_anytime_td' ||
                         selectedMarket === 'player_last_td' ||
                         selectedMarket.includes('scorer') ||
                         selectedMarket.includes('first_basket');

  // Apply tier filtering (but NOT for yards markets or binary markets)
  if (isYardsMarket || isBinaryMarket) {
    // For yards markets and binary markets, show all lines (no tier filtering)
    filteredData = filteredData;
  } else if (masterTierFilter !== null) {
    // When tier filter is set, filter to the selected tier
    filteredData = filteredData.filter(player =>
      player.tierPoints === parseFloat(masterTierFilter) ||
      player.point === parseFloat(masterTierFilter)
    );
  } else if (!['h2h', 'totals', 'spreads'].includes(selectedMarket)) {
    // For TDS and other player markets, when tiers are off, show specific tiers
    filteredData = showTiers ? filteredData : filteredData.filter(player => {
      // For TDS markets specifically, show 0.5 when tiers are off
      if (selectedMarket === 'player_tds_over' ||
          selectedMarket === 'player_pass_tds') {
        return player.point === 0.5;
      }
      // For other markets, show point <= 1
      return player.point <= 1 || player.tierPoints <= 1;
    });
  }

  // Helper function to find best odds and use pre-calculated edge data
  const findBestOdds = (player) => {
    let bestOdds = null;
    let bestBook = null;
    let bookCount = 0;

    // Check if this is a binary market (no _over suffix) vs over/under market
    const isBinary = selectedMarket === 'player_1st_td' ||
                     selectedMarket === 'player_anytime_td' ||
                     selectedMarket === 'player_last_td' ||
                     selectedMarket.includes('scorer') ||
                     selectedMarket.includes('first_basket');

    allBookmakers.forEach(book => {
      // Handle bookmaker aliases (e.g., williamhill_us -> caesars)
      const bookAlias = BOOKMAKER_ALIASES[book] || book;

      // For binary markets, use direct field; for O/U markets (including TDs), check _over first
      // Try both the original book name and its alias
      let odds;
      if (isBinary) {
        odds = player[`${selectedMarket}_${bookAlias}`] || player[`${selectedMarket}_${book}`];
      } else {
        odds = player[`${selectedMarket}_${bookAlias}_over`] ||
               player[`${selectedMarket}_${book}_over`] ||
               player[`${selectedMarket}_${bookAlias}`] ||
               player[`${selectedMarket}_${book}`];
      }

      if (odds !== null && odds !== undefined) {
        bookCount++;
        if (!bestOdds || odds > bestOdds) {
          bestOdds = odds;
          bestBook = book;
        }
      }
    });

    // Use pre-calculated edge data from the API
    const bestBookPrice = player[`${selectedMarket}_bestBookPrice`];
    const bestBookDiff = player[`${selectedMarket}_bestBookDiff`];

    return { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount };
  };


  // Sort the filtered data
  const sortedData = [...filteredData].sort((a, b) => {
    // First by team
    if (a.team !== b.team) {
      if (a.team === safeGame.away) return -1;
      if (b.team === safeGame.away) return 1;
      if (a.team === safeGame.home) return -1;
      if (b.team === safeGame.home) return 1;
    }

    // For TDS markets when showTiers is true, sort by position like desktop
    const isTdsMarket = selectedMarket === 'player_tds_over' ||
                        selectedMarket === 'player_pass_tds';

    if (isTdsMarket && showTiers) {
      // Within same team, sort by position first (copied from desktop NFLDataTable)
      const getPlayerPosition = (player) => {
        // Get position abbreviation for sorting
        const position = player.position?.abbreviation || player.positionCode;
        if (!position) return 'ZZ'; // Put players without positions at the end

        // Define NFL position priority order
        const positionOrder = {
          // Offense
          'QB': 1,
          'RB': 2, 'FB': 3,
          'WR': 4, 'TE': 5,
          'T': 6, 'G': 7, 'C': 8,
          'OT': 6, 'OG': 7, 'OL': 8,

          // Defense
          'DE': 10, 'DT': 11, 'NT': 12,
          'OLB': 13, 'MLB': 14, 'ILB': 15, 'LB': 16,
          'CB': 17, 'S': 18, 'FS': 19, 'SS': 20,
          'DB': 21,

          // Special Teams
          'K': 25, 'P': 26, 'LS': 27, 'KR': 28, 'PR': 29,
          'D/ST': 30, 'DEF': 30
        };

        return positionOrder[position] || 99; // Unknown positions go to end
      };

      const positionA = getPlayerPosition(a);
      const positionB = getPlayerPosition(b);

      if (positionA !== positionB) {
        return positionA - positionB;
      }

      // Within same team and position, sort by name
      const nameA = a.name || '';
      const nameB = b.name || '';
      const nameComparison = nameA.localeCompare(nameB);

      // If names are the same, sort by point value
      if (nameComparison === 0) {
        const pointA = a.point || 0;
        const pointB = b.point || 0;
        return pointA - pointB;
      }

      return nameComparison;
    }

    // Default sorting: by best odds (lowest to highest)
    const aOdds = findBestOdds(a).bestOdds;
    const bOdds = findBestOdds(b).bestOdds;

    // Put null odds at the end
    if (aOdds === null && bOdds === null) return 0;
    if (aOdds === null) return 1;
    if (bOdds === null) return -1;

    // Sort by odds value (lowest first)
    if (aOdds !== bOdds) return aOdds - bOdds;

    // If odds are the same, sort by name
    const nameCompare = (a.name || '').localeCompare(b.name || '');
    if (nameCompare !== 0) return nameCompare;

    // Then by point value
    return (a.point || 0) - (b.point || 0);
  });

  // Organize players by team
  const playersByTeam = {};

  sortedData.forEach(player => {
    let teamKey = 'unknown';
    let teamName = 'Unknown Team';
    let teamLogo = null;

    if (player.team === safeGame.away || (player.teamId && player.teamId === safeGame.awayTeamId)) {
      teamKey = 'away';
      teamName = safeGame.awayTeamFull || safeGame.away;
      teamLogo = safeGame.awayLogo || getTeamLogoUrl(safeGame.away);
    } else if (player.team === safeGame.home || (player.teamId && player.teamId === safeGame.homeTeamId)) {
      teamKey = 'home';
      teamName = safeGame.homeTeamFull || safeGame.home;
      teamLogo = safeGame.homeLogo || getTeamLogoUrl(safeGame.home);
    } else {
      teamKey = player.team || 'unknown';
      teamName = player.team || 'Unknown Team';
      teamLogo = player.team ? getTeamLogoUrlDirect(player.team, false, 'nfl') : null;
    }

    if (!playersByTeam[teamKey]) {
      playersByTeam[teamKey] = {
        name: teamName,
        logo: teamLogo,
        players: []
      };
    }

    playersByTeam[teamKey].players.push(player);
  });

  return (
    <div className="table-group">
      <GameHeader
        gameTime={gameTime}
        gameStatus={gameStatus}
        game={game}
        getTeamLogoUrl={getTeamLogoUrl}
        isMobile={true}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        formatLiveStatus={formatNFLLiveStatus}
            isLoading={showLoading}
        showScores={true}
            isLoading={showLoading}
      />

      {!isCollapsed && (
        <div className="table-container">
          {/* Show All Tiers Toggle for TDS markets */}
          {(selectedMarket === 'player_tds_over' ||
            selectedMarket === 'player_pass_tds') && (
            <div className="d-flex justify-content-center mb-2">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="mobile-tds-tiers-toggle"
                  checked={showTiers}
                  onChange={(e) => onTierToggle && onTierToggle(e.target.checked)}
                />
                <label
                  className="form-check-label text-white"
                  htmlFor="mobile-tds-tiers-toggle"
                  style={{ fontSize: '12px', marginLeft: '4px' }}
                >
                  Show All Tiers
                </label>
              </div>
            </div>
          )}

          {isYardsMarket && nflViewMode === 'middles' ? (() => {
            // Middles view — card list per team, matching the desktop layout.
            // Player-name column left, U/O rows middle (with all tied book
            // logos), 1u max win / max loss right. Same picks as desktop:
            //   over  → lowest point, tiebreak best odds, ≥ -120
            //   under → highest point, tiebreak best odds, ≥ -120
            //   requires under.point > over.point (a real middle).
            const priceToProfit = (o) => {
              const n = Number(o);
              if (!Number.isFinite(n) || n === 0) return 0;
              return n > 0 ? n / 100 : 100 / Math.abs(n);
            };
            const fmtOdds = (o) => (Number(o) > 0 ? `+${o}` : `${o}`);
            const fmtUnit = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}u`;

            // Restrict to books enabled for Best Bets so users only see
            // middles between their preferred sportsbooks.
            const middlesBooks = allBookmakers.filter(b => isBookEnabledForBestBets(b));

            // For each unique player in this team, collect every valid
            // (over × under) middle combination — one card per combo. Lines/
            // books flagged with _isAvailable === false are excluded — an
            // unavailable price can't complete a middle bet.
            const collectSideByPoint = (allPlayerLines) => {
              const overByPoint = new Map();
              const underByPoint = new Map();
              allPlayerLines.forEach(line => {
                if (line.isAlternate) return;
                middlesBooks.forEach(book => {
                  const point = line[`${selectedMarket}_${book}_point`] ?? line.point;
                  if (point == null) return;
                  const bookAvail = line[`${selectedMarket}_${book}_isAvailable`] !== false;
                  const overAvail = bookAvail && line[`${selectedMarket}_${book}_over_isAvailable`] !== false;
                  const underAvail = bookAvail && line[`${selectedMarket}_${book}_under_isAvailable`] !== false;
                  const oOdds = line[`${selectedMarket}_${book}_over`];
                  const uOdds = line[`${selectedMarket}_${book}_under`];
                  if (overAvail && oOdds !== null && oOdds !== undefined && Number(oOdds) >= -120) {
                    const cur = overByPoint.get(point);
                    if (!cur || Number(oOdds) > Number(cur.odds)) {
                      overByPoint.set(point, { point, odds: oOdds, books: [book] });
                    } else if (Number(oOdds) === Number(cur.odds) && !cur.books.includes(book)) {
                      cur.books.push(book);
                    }
                  }
                  if (underAvail && uOdds !== null && uOdds !== undefined && Number(uOdds) >= -120) {
                    const cur = underByPoint.get(point);
                    if (!cur || Number(uOdds) > Number(cur.odds)) {
                      underByPoint.set(point, { point, odds: uOdds, books: [book] });
                    } else if (Number(uOdds) === Number(cur.odds) && !cur.books.includes(book)) {
                      cur.books.push(book);
                    }
                  }
                });
              });
              return { overs: [...overByPoint.values()], unders: [...underByPoint.values()] };
            };

            const hasEligibleMainlines = (allPlayerLines) => {
              for (const line of allPlayerLines) {
                if (line.isAlternate) continue;
                for (const b of middlesBooks) {
                  const u = line[`${selectedMarket}_${b}_under`];
                  const uAvail = line[`${selectedMarket}_${b}_isAvailable`] !== false && line[`${selectedMarket}_${b}_under_isAvailable`] !== false;
                  if (u !== null && u !== undefined && uAvail) return true;
                }
              }
              return false;
            };

            const computeMiddles = (players) => {
              // One entry per unique player name → array of middle combos.
              const seen = new Set();
              const cards = [];
              players.forEach(player => {
                if (seen.has(player.name)) return;
                seen.add(player.name);
                const allPlayerLines = players.filter(p => p.name === player.name);
                if (!hasEligibleMainlines(allPlayerLines)) return;
                const { overs, unders } = collectSideByPoint(allPlayerLines);
                overs.forEach(o => {
                  unders.forEach(u => {
                    if (u.point <= o.point) return;
                    // A real middle needs different books on the two sides.
                    // Skip when both tied-book lists collapse to the same
                    // single book.
                    const bookUnion = new Set([...o.books, ...u.books]);
                    if (bookUnion.size < 2) return;
                    const pO = priceToProfit(o.odds);
                    const pU = priceToProfit(u.odds);
                    cards.push({
                      player,
                      bestOver: o,
                      bestUnder: u,
                      maxProfit: pO + pU,
                      maxLoss: Math.min(pO, pU) - 1,
                      width: u.point - o.point,
                    });
                  });
                });
              });
              return cards.sort((a, b) => (b.maxProfit - a.maxProfit) || (b.width - a.width));
            };

            const anyMiddles = Object.values(playersByTeam)
              .some(t => computeMiddles(t.players).length > 0);
            if (!anyMiddles) {
              return (
                <div style={{ color: '#6b7280', fontSize: '0.8rem', padding: '16px', textAlign: 'center' }}>
                  No qualifying middle opportunities.
                </div>
              );
            }

            return Object.entries(playersByTeam).map(([teamKey, teamData], teamIndex) => {
              const middles = computeMiddles(teamData.players);
              if (middles.length === 0) return null;

              return (
                <div key={teamKey} style={{ marginBottom: teamIndex < Object.keys(playersByTeam).length - 1 ? '16px' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    {teamData.logo && (
                      <img src={teamData.logo.startsWith('http') ? teamData.logo : getTeamLogoUrlDirect(teamData.logo, false, 'nfl')} alt={teamKey} style={{ width: 20, height: 20 }} onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                    )}
                    <span style={{ color: '#cbd5e1', fontSize: '0.95rem', fontWeight: 600 }}>{teamData.name}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {middles.map((m, idx) => {
                      const pos = m.player.position?.abbreviation || m.player.positionCode || '';
                      return (
                        <div
                          key={`middle-${m.player.name}-${idx}-${m.bestOver.point}-${m.bestUnder.point}`}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1.2fr 1.8fr 0.9fr',
                            gap: '10px',
                            padding: '10px 12px',
                            backgroundColor: 'rgba(255,255,255,0.03)',
                            border: '1px solid #2a2d31',
                            borderRadius: '6px',
                            alignItems: 'center',
                          }}
                        >
                          {/* Left: player */}
                          <div style={{ minWidth: 0 }}>
                            <div
                              onClick={() => handlePlayerClick(m.player.name, m.player.team, pos, m.player.playerId)}
                              style={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {formatPlayerName(m.player.name, true)}
                            </div>
                            {pos && <div style={{ color: '#9ca3af', fontSize: '0.78rem' }}>{pos}</div>}
                          </div>

                          {/* Middle: U (top) / O (bottom) */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(239, 68, 68, 0.12)', padding: '5px 8px', borderRadius: '4px' }}>
                              <span style={{ color: '#f87171', fontWeight: 700, fontSize: '0.8rem', minWidth: '46px' }}>U {m.bestUnder.point}</span>
                              <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>{fmtOdds(m.bestUnder.odds)}</span>
                              <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                                {m.bestUnder.books.slice(0, 4).map(b => (
                                  <img key={b} src={getBookLogoUrl(b)} alt={b} title={b} style={{ height: 16 }} onError={(e) => { e.target.style.display = 'none'; }} />
                                ))}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(34, 197, 94, 0.12)', padding: '5px 8px', borderRadius: '4px' }}>
                              <span style={{ color: '#4ade80', fontWeight: 700, fontSize: '0.8rem', minWidth: '46px' }}>O {m.bestOver.point}</span>
                              <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>{fmtOdds(m.bestOver.odds)}</span>
                              <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                                {m.bestOver.books.slice(0, 4).map(b => (
                                  <img key={b} src={getBookLogoUrl(b)} alt={b} title={b} style={{ height: 16 }} onError={(e) => { e.target.style.display = 'none'; }} />
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Right: max win / max loss */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px' }}>
                            <div style={{ color: '#4ade80', fontSize: '0.9rem', fontWeight: 700 }}>{fmtUnit(m.maxProfit)}</div>
                            <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>max win</div>
                            <div style={{ color: m.maxLoss >= 0 ? '#4ade80' : '#f87171', fontSize: '0.9rem', fontWeight: 700, marginTop: '3px' }}>{fmtUnit(m.maxLoss)}</div>
                            <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>max loss</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })() : Object.entries(playersByTeam).map(([teamKey, teamData], teamIndex) => (
            <div key={teamKey} style={{ marginBottom: teamIndex < Object.keys(playersByTeam).length - 1 ? '16px' : '0' }}>
              {isYardsMarket && (
                <style>
                  {`
                    .yards-market-table tr {
                      border-bottom: 0 !important;
                    }
                    .yards-market-table tr td {
                      border-top: 0 !important;
                      border-bottom: 0 !important;
                    }
                    .yards-market-table tbody tr td {
                      border-top: 0 !important;
                      border-bottom: 0 !important;
                    }
                    .yards-market-table td:not(:last-child) {
                      border-right: none !important;
                    }
                    .yards-market-table td:not(:first-child) {
                      border-left: none !important;
                    }
                    .yards-market-table th:not(:last-child) {
                      border-right: none !important;
                    }
                    .yards-market-table th:not(:first-child) {
                      border-left: none !important;
                    }
                  `}
                </style>
              )}
              <Table bordered size="sm" className={`text-center ${isYardsMarket ? 'yards-market-table' : ''}`}>
                <thead>
                  <tr>
                    {isYardsMarket ? (
                      nflViewMode === 'bestline' ? (
                        // Edge View - show book logos as column headers
                        <>
                          <th style={{ width: '40%', fontSize: '0.75rem' }}>NAME</th>
                          {visibleBookmakers.map(book => {
                            const bookLink = game?.sgoLinks?.bookmakers?.[book];
                            return (
                              <th key={book} style={{ padding: '4px', textAlign: 'center' }}>
                                {bookLink ? (
                                  <a
                                    href={bookLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      display: 'inline-block',
                                      touchAction: 'auto',
                                      WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                                      padding: '4px'
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onTouchEnd={(e) => e.stopPropagation()}
                                  >
                                    <img
                                      src={getBookLogoUrl(book)}
                                      alt={book}
                                      style={{ height: '16px', display: 'block' }}
                                    />
                                  </a>
                                ) : (
                                  <img
                                    src={getBookLogoUrl(book)}
                                    alt={book}
                                    style={{ height: '16px' }}
                                  />
                                )}
                              </th>
                            );
                          })}
                        </>
                      ) : (
                        // O/U View - 4-column layout for yards markets
                        <>
                          <th style={{ width: '50%', fontSize: '0.75rem', borderRight: 'none' }}>PLAYER</th>
                          <th style={{ width: '15%', fontSize: '0.75rem', borderRight: 'none', borderLeft: 'none' }}></th>
                          <th style={{ width: '20%', fontSize: '0.75rem', borderLeft: 'none', borderRight: 'none' }}>O/U</th>
                          <th style={{ width: '15%', fontSize: '0.75rem', borderLeft: 'none' }}>ODDS</th>
                        </>
                      )
                    ) : (
                      // Original 3-column layout for other markets
                      <>
                        <th style={{ width: '50%', fontSize: '0.75rem' }}>NAME</th>
                        <th colSpan={2} style={{ width: '50%', fontSize: '0.75rem' }}>BEST BOOKS</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let uniquePlayerIndex = 0;
                    let lastPlayerName = '';

                    // For yards markets, deduplicate players first
                    const playersToRender = isYardsMarket
                      ? teamData.players.filter((player, index, array) =>
                          index === array.findIndex(p => p.name === player.name))
                      : teamData.players;

                    return playersToRender.map((player, index) => {
                      const isFirstRowForPlayer = index === 0 || playersToRender[index - 1].name !== player.name;
                      const { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount } = findBestOdds(player);

                      // Increment unique player index only when we encounter a new player
                      if (player.name !== lastPlayerName) {
                        if (lastPlayerName !== '') uniquePlayerIndex++;
                        lastPlayerName = player.name;
                      }

                      // Determine background color based on unique player index
                      const bgColor = (uniquePlayerIndex % 2 === 0) ? 'rgb(26, 26, 26)' : 'rgb(45, 45, 45)';

                      if (isYardsMarket) {
                        // Find ALL lines for this player across all point values
                        const allPlayerLines = teamData.players.filter(p => p.name === player.name);

                        // Edge View - show all books with best edge for each
                        if (nflViewMode === 'bestline') {
                          // Get position info
                          const position = player.position?.abbreviation || player.positionCode || '';

                          return (
                            <React.Fragment key={player.tierKey || player.id || `${player.name}-${index}`}>
                              <tr style={{ backgroundColor: bgColor }}>
                                <td style={{ textAlign: 'left', padding: '6px', fontSize: '0.75rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <img
                                      src={teamData.logo?.startsWith('http') ? teamData.logo : getTeamLogoUrlDirect(teamData.logo || teamKey, false, 'nfl')}
                                      alt={teamData.logo || teamKey}
                                      style={{ width: '14px', height: '14px' }}
                                      onError={(e) => {
                                        e.target.style.visibility = 'hidden';
                                        e.target.style.width = '0px';
                                      }}
                                    />
                                    <span
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => handlePlayerClick(player.name, player.team, player.position?.abbreviation || player.positionCode, player.playerId)}
                                    >
                                      {formatPlayerName(player.name, true)}
                                    </span>
                                    {position && (
                                      <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                        ({position})
                                      </span>
                                    )}
                                  </div>
                                </td>
                                {visibleBookmakers.map(book => {
                                  // Pass all lines for this player to find the best edge
                                  const bestLine = getBestLineByEdge(allPlayerLines, book, selectedMarket);
                                  if (!bestLine) {
                                    return (
                                      <td key={book} style={{ padding: '2px', textAlign: 'center', fontSize: '0.7rem' }}>
                                        <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>-</span>
                                      </td>
                                    );
                                  }

                                  const { odds, point, side, bestBookPrice, bestBookDiff } = bestLine;

                                  // Validate that we have valid odds
                                  if (odds === null || odds === undefined) {
                                    return (
                                      <td key={book} style={{ padding: '2px', textAlign: 'center', fontSize: '0.7rem' }}>
                                        <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>-</span>
                                      </td>
                                    );
                                  }

                                  // Apply tier styling using the edge data from API
                                  const baseCellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, visibleBookmakers.length, allPlayerLines, book, selectedMarket);

                                  return (
                                    <td
                                      key={book}
                                      className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, point, side)}
                                      style={{ padding: '2px', textAlign: 'center', fontSize: '0.7rem', cursor: 'pointer' }}
                                      onClick={() => onOddsClick && onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position?.abbreviation || player.positionCode, point, side)}
                                    >
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', ...baseCellStyle, padding: '4px', borderRadius: '3px' }}>
                                        <span style={{ fontSize: '0.65rem' }}>
                                          {side === 'over' ? `o${point}` : `u${point}`}
                                        </span>
                                        <span style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                                          {odds > 0 ? `+${odds}` : odds}
                                        </span>
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            </React.Fragment>
                          );
                        }

                        // O/U / Middles view — walk this player's lines and
                        // pick the best over (lowest point) + best under
                        // (highest point) to surface middle opportunities.
                        //
                        // Eligibility gate differs by mode:
                        //   'ou'       → strict, require line.isAlternate === false
                        //   'middles'  → empirical, keep any line where at least
                        //                one book quoted an under (older logic
                        //                that surfaced natural middles across
                        //                stagger-mainline books).
                        let bestMainLineOver = null;
                        let bestMainLineOverBook = null;
                        let bestMainLineOverPoint = null;
                        let bestMainLineOverEdge = null;
                        let bestMainLineOverBestPrice = null;
                        let bestMainLineOverIsAvailable = true;

                        let bestMainLineUnder = null;
                        let bestMainLineUnderBook = null;
                        let bestMainLineUnderPoint = null;
                        let bestMainLineUnderEdge = null;
                        let bestMainLineUnderBestPrice = null;
                        let bestMainLineUnderIsAvailable = true;

                        // Check each line for this player
                        allPlayerLines.forEach(line => {
                          if (nflViewMode === 'middles') {
                            // Empirical mainline detector — line qualifies if
                            // any book offers an under on it.
                            let hasUnderOdds = false;
                            for (const b of allBookmakers) {
                              const u = line[`${selectedMarket}_${b}_under`];
                              if (u !== null && u !== undefined) { hasUnderOdds = true; break; }
                            }
                            if (!hasUnderOdds) return;
                          } else if (line.isAlternate) {
                            return;
                          }

                          // Get edge data for this line
                          const bestBookPrice = line[`${selectedMarket}_bestBookPrice`];
                          const bestBookDiff = line[`${selectedMarket}_bestBookDiff`];

                          // Find best over and under for this specific line
                          allBookmakers.forEach(book => {
                            const overOdds = line[`${selectedMarket}_${book}_over`] || line[`${selectedMarket}_${book}`];
                            const underOdds = line[`${selectedMarket}_${book}_under`];

                            // Check for best over odds on main lines
                            if (overOdds !== null && overOdds !== undefined) {
                              // In Middles mode reject heavy-favorite overs
                              // (< -120 American) — same rationale as unders:
                              // extreme quotes on outlier points dominate the
                              // "lowest point" pick and hide real middles.
                              if (nflViewMode === 'middles' && Number(overOdds) < -120) return;
                              // Prefer smallest point value for over (to see middle opportunities), then best odds
                              if (!bestMainLineOver ||
                                  line.point < bestMainLineOverPoint ||
                                  (line.point === bestMainLineOverPoint && overOdds > bestMainLineOver)) {
                                bestMainLineOver = overOdds;
                                bestMainLineOverBook = book;
                                bestMainLineOverPoint = line.point;
                                bestMainLineOverEdge = bestBookDiff;
                                bestMainLineOverBestPrice = bestBookPrice;
                                bestMainLineOverIsAvailable = line[`${selectedMarket}_${book}_over_isAvailable`] !== false && line[`${selectedMarket}_${book}_isAvailable`] !== false;
                              }
                            }

                            // Check for best under odds on main lines
                            if (underOdds !== null && underOdds !== undefined) {
                              // In Middles mode reject heavy-favorite unders
                              // (< -120 American). Books like Kalshi post very
                              // negative unders on outlier points that
                              // otherwise dominate the "highest point"
                              // pick and hide real middle opportunities.
                              if (nflViewMode === 'middles' && Number(underOdds) < -120) return;
                              // Prefer largest point value for under (to see middle opportunities), then best odds
                              if (!bestMainLineUnder ||
                                  line.point > bestMainLineUnderPoint ||
                                  (line.point === bestMainLineUnderPoint && underOdds > bestMainLineUnder)) {
                                bestMainLineUnder = underOdds;
                                bestMainLineUnderBook = book;
                                bestMainLineUnderPoint = line.point;
                                bestMainLineUnderEdge = bestBookDiff;
                                bestMainLineUnderBestPrice = bestBookPrice;
                                bestMainLineUnderIsAvailable = line[`${selectedMarket}_${book}_under_isAvailable`] !== false;
                              }
                            }
                          });
                        });

                        // Skip if we don't have at least one valid line
                        if (!bestMainLineOver && !bestMainLineUnder) {
                          return null;
                        }

                        // Get position info
                        const position = player.position?.abbreviation || player.positionCode || '';

                        return (
                          <React.Fragment key={player.tierKey || player.id || `${player.name}-${index}`}>
                            {/* First row: Player info and lowest over */}
                            <tr style={{ backgroundColor: bgColor }}>
                              <td style={{ textAlign: 'left', padding: '6px', fontSize: '0.75rem', borderRight: 'none !important', borderBottom: 'none !important' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <img
                                    src={teamData.logo?.startsWith('http') ? teamData.logo : getTeamLogoUrlDirect(teamData.logo || teamKey, false, 'nfl')}
                                    alt={teamData.logo || teamKey}
                                    style={{ width: '14px', height: '14px' }}
                                    onError={(e) => {
                                      e.target.style.visibility = 'hidden';
                                      e.target.style.width = '0px';
                                    }}
                                  />
                                  <span
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handlePlayerClick(player.name, player.team, player.position?.abbreviation || player.positionCode, player.playerId)}
                                  >
                                    {formatPlayerName(player.name, true)}
                                  </span>
                                  {position && (
                                    <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                      ({position})
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td style={{ padding: '2px', textAlign: 'center', borderRight: 'none', borderLeft: 'none', borderBottom: 'none' }}>
                                {bestMainLineOverBook && (
                                  <img
                                    src={getBookLogoUrl(bestMainLineOverBook)}
                                    alt={bestMainLineOverBook}
                                    style={{ height: '14px' }}
                                  />
                                )}
                              </td>

                              {(() => {
                                const baseCellStyle = bestMainLineOver ? getCellStyle(bestMainLineOver, bestMainLineOverBestPrice, bestMainLineOverEdge, visibleBookmakers.length, allPlayerLines, bestMainLineOverBook, selectedMarket) : {};
                                return (
                                  <>
                                    <td style={{ padding: '2px 2px 0px 2px', textAlign: 'center', fontSize: '0.7rem', borderLeft: 'none', borderRight: 'none', borderBottom: 'none', opacity: bestMainLineOverIsAvailable === false ? 0.25 : undefined, ...baseCellStyle }}>
                                      {bestMainLineOverPoint !== null && (
                                        <span style={{ fontWeight: 'bold' }}>
                                          O {bestMainLineOverPoint}
                                        </span>
                                      )}
                                    </td>

                                    <td
                                      className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, bestMainLineOverBook, bestMainLineOverPoint, 'over')}
                                      style={{ padding: '2px 2px 0px 2px', textAlign: 'center', fontSize: '0.7rem', borderLeft: 'none', borderBottom: 'none', cursor: bestMainLineOver ? 'pointer' : 'default', opacity: bestMainLineOverIsAvailable === false ? 0.25 : undefined, ...baseCellStyle }}
                                      onClick={() => {
                                        if (onOddsClick && bestMainLineOver) {
                                          onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestMainLineOverBook, bestMainLineOver, safeGame.away, safeGame.home, player.team, player.position?.abbreviation || player.positionCode);
                                        }
                                      }}
                                    >
                                      {bestMainLineOver && (
                                        <span style={{ fontWeight: 'bold' }}>
                                          {bestMainLineOver > 0 ? `+${bestMainLineOver}` : bestMainLineOver}
                                        </span>
                                      )}
                                    </td>
                                  </>
                                );
                              })()}
                            </tr>

                            {/* Second row: Highest under - only show if we have under odds */}
                            {bestMainLineUnder && (
                              <tr style={{ backgroundColor: bgColor, borderTop: 'none' }}>
                                <td style={{ padding: '6px', borderRight: 'none', borderTop: 'none' }}></td>

                                <td style={{ padding: '2px', textAlign: 'center', borderRight: 'none', borderLeft: 'none', borderTop: 'none' }}>
                                  {bestMainLineUnderBook && (
                                    <img
                                      src={getBookLogoUrl(bestMainLineUnderBook)}
                                      alt={bestMainLineUnderBook}
                                      style={{ height: '14px' }}
                                    />
                                  )}
                                </td>

                                {(() => {
                                  const baseCellStyle = getCellStyle(bestMainLineUnder, bestMainLineUnderBestPrice, bestMainLineUnderEdge, visibleBookmakers.length, allPlayerLines, bestMainLineUnderBook, selectedMarket);
                                  return (
                                    <>
                                      <td style={{ padding: '0px 2px 2px 2px', textAlign: 'center', fontSize: '0.7rem', borderLeft: 'none', borderRight: 'none', borderTop: 'none', opacity: bestMainLineUnderIsAvailable === false ? 0.25 : undefined, ...baseCellStyle }}>
                                        <span style={{ fontWeight: 'bold' }}>
                                          U {bestMainLineUnderPoint}
                                        </span>
                                      </td>

                                      <td
                                        className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, bestMainLineUnderBook, bestMainLineUnderPoint, 'under')}
                                        style={{ padding: '0px 2px 2px 2px', textAlign: 'center', fontSize: '0.7rem', borderLeft: 'none', borderTop: 'none', cursor: 'pointer', opacity: bestMainLineUnderIsAvailable === false ? 0.25 : undefined, ...baseCellStyle }}
                                        onClick={() => {
                                          if (onOddsClick) {
                                            onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestMainLineUnderBook, bestMainLineUnder, safeGame.away, safeGame.home, player.team, player.position?.abbreviation || player.positionCode);
                                          }
                                        }}
                                      >
                                        <span style={{ fontWeight: 'bold' }}>
                                          {bestMainLineUnder > 0 ? `+${bestMainLineUnder}` : bestMainLineUnder}
                                        </span>
                                      </td>
                                    </>
                                  );
                                })()}
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      } else {
                        // Original 3-column layout for non-yards markets
                        return (
                          <React.Fragment key={player.tierKey || player.id || `${player.name}-${index}`}>
                            <tr style={{ backgroundColor: bgColor }}>
                              <td style={{ textAlign: 'left', padding: '6px', fontSize: '0.75rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {isFirstRowForPlayer && (
                                      <>
                                        <img
                                          src={teamData.logo?.startsWith('http') ? teamData.logo : getTeamLogoUrlDirect(teamData.logo || teamKey, false, 'nfl')}
                                          alt={teamData.logo || teamKey}
                                          style={{ width: '14px', height: '14px' }}
                                          onError={(e) => {
                                            e.target.style.visibility = 'hidden';
                                            e.target.style.width = '0px';
                                          }}
                                        />
                                        <span
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => handlePlayerClick(player.name, player.team, player.position?.abbreviation || player.positionCode, player.playerId)}
                                        >
                                          {formatPlayerName(player.name, true)}
                                        </span>
                                        {(player.position?.abbreviation || player.positionCode) && (
                                          <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                            ({player.position?.abbreviation || player.positionCode})
                                          </span>
                                        )}
                                      </>
                                    )}
                                    {/* Don't show player name for additional tiers - leave empty */}
                                  </div>
                                  {player.point !== null && player.point !== undefined && (
                                    <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                      {player.point === 'yes' ? 'Yes' :
                                       player.point === 'no' ? 'No' :
                                       player.point}
                                    </span>
                                  )}
                                </div>
                              </td>
                              {bestOdds !== null && bestBook ? (
                                <>
                                  <td style={{ padding: '4px', textAlign: 'center', width: '60px' }}>
                                    <img
                                      src={getBookLogoUrl(bestBook)}
                                      alt={bestBook}
                                      style={{ height: '16px' }}
                                    />
                                  </td>
                                  <td
                                    className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, bestBook, player.point, 'yes')}
                                    style={{ padding: '6px', textAlign: 'center', cursor: 'pointer' }}
                                    onClick={() => {
                                      if (onOddsClick) {
                                        onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestBook, bestOdds, safeGame.away, safeGame.home, player.team, player.position?.abbreviation || player.positionCode);
                                      }
                                    }}
                                  >
                                    <span style={getCellStyle(bestOdds, bestBookPrice, bestBookDiff, bookCount)}>
                                      {bestOdds > 0 ? `+${bestOdds}` : bestOdds}
                                    </span>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td style={{ padding: '4px' }}></td>
                                  <td style={{ padding: '6px' }}></td>
                                </>
                              )}
                            </tr>
                          </React.Fragment>
                        );
                      }
                    });
                  })()}
                </tbody>
              </Table>
            </div>
          ))}

          {/* Mobile Book Pagination for Player Props */}
          {totalPages > 1 && !isCollapsed && !isBinaryMarket && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '15px',
              marginTop: '10px',
              marginBottom: '10px'
            }}>
              <button
                onClick={() => setMobileBookIndex(Math.max(0, mobileBookIndex - 1))}
                disabled={mobileBookIndex === 0}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: mobileBookIndex === 0 ? 'default' : 'pointer',
                  opacity: mobileBookIndex === 0 ? 0.3 : 1,
                  padding: '5px'
                }}
              >
                ‹
              </button>

              <div style={{ display: 'flex', gap: '8px' }}>
                {[...Array(totalPages)].map((_, index) => (
                  <div
                    key={index}
                    onClick={() => setMobileBookIndex(index)}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: index === mobileBookIndex ? '#007bff' : '#dee2e6',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                  />
                ))}
              </div>

              <button
                onClick={() => setMobileBookIndex(Math.min(totalPages - 1, mobileBookIndex + 1))}
                disabled={mobileBookIndex === totalPages - 1}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: mobileBookIndex === totalPages - 1 ? 'default' : 'pointer',
                  opacity: mobileBookIndex === totalPages - 1 ? 0.3 : 1,
                  padding: '5px'
                }}
              >
                ›
              </button>
            </div>
          )}
        </div>
      )}

      {/* NFL Player Modal */}
      {selectedPlayer && (
        <NFLPlayerModal
          show={showPlayerModal}
          onHide={handleClosePlayerModal}
          playerName={selectedPlayer.playerName}
          playerTeam={selectedPlayer.playerTeam}
          playerPosition={selectedPlayer.playerPosition}
          playerId={selectedPlayer.playerId}
          gameId={game?.gameIDmlb}
          getTeamLogoUrl={getTeamLogoUrl}
          getBookLogoUrl={getBookLogoUrl}
          onOddsClick={onOddsClick}
          bookmakers={allBookmakers}
        />
      )}
    </div>
  );
};

export default MobileNFLDataTable;